import type { GlobalSettings } from '../../engine/types';
import type { AnswerTileData } from './AnswerStrip';

/**
 * Idle reset and recent calculations for the single-calculation pages.
 *
 * Every calculator page keeps its inputs in localStorage under its own key
 * prefix (usePersistedState). After IDLE_RESET_MS with no input on a page, its
 * keys are cleared so the next visit starts from the defaults; before they are
 * cleared the values are kept as a "recent" so one click brings them back.
 */

export const IDLE_RESET_MS = 10 * 60 * 1000;
/** Recents shown under the results banner. */
export const RECENTS_SHOWN = 3;
/** One more than shown: the calculation on screen is usually the newest and is not listed. */
const RECENTS_KEPT = RECENTS_SHOWN + 1;

type SharedSetting = 'voltage' | 'phase' | 'conduitType';

export interface SessionCalculator {
  /** localStorage prefix of the page's inputs. */
  prefix: string;
  /** Keys under the prefix that are saved data, not inputs (never cleared or snapshotted). */
  keep?: string[];
  /**
   * App-wide settings the page edits inline. They are saved with a recent so it
   * reloads faithfully, but the idle reset leaves them alone: they are the
   * user's Settings-panel defaults and other pages share them.
   */
  settings?: SharedSetting[];
}

/** Calculator section id (App.tsx) → where its inputs live. */
export const SESSION_CALCULATORS: Record<string, SessionCalculator> = {
  ampacity: { prefix: 'ampacity-' },
  conduit: { prefix: 'conduit-sizing-', settings: ['conduitType'] },
  'voltage-drop': { prefix: 'vd-' },
  motor: { prefix: 'motor-' },
  feeder: { prefix: 'feeder-', keep: ['feeder-saved-configs'], settings: ['voltage', 'phase', 'conduitType'] },
  'multi-motor-feeder': { prefix: 'mmf-' },
  transformer: { prefix: 'transformer-', settings: ['conduitType'] },
  generator: { prefix: 'generator-', settings: ['conduitType'] },
  'hazardous-locations': { prefix: 'hazloc-' },
};

export interface RecentCalculation {
  id: string;
  /** When the calculation was last worked on (ms). */
  at: number;
  /** Inputs summary, e.g. "75 kVA · 480 → 208Y/120 V". */
  label: string;
  /** Headline results, e.g. "Primary FLA 90.2 A · Secondary FLA 208 A". */
  result: string;
  /** Raw localStorage values of the page's inputs. */
  values: Record<string, string>;
  settings?: Partial<Pick<GlobalSettings, SharedSetting>>;
}

const lastActivityKey = (id: string) => `bgd-calc-session:${id}:last-activity`;
const recentsKey = (id: string) => `bgd-calc-session:${id}:recents`;
const defaultsKey = (id: string) => `bgd-calc-session:${id}:defaults`;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage full or blocked: the page still works, it just forgets.
  }
}

function storageKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
  } catch {
    // Storage blocked.
  }
  return keys;
}

function inputKeys(cfg: SessionCalculator): string[] {
  return storageKeys().filter(k => k.startsWith(cfg.prefix) && !cfg.keep?.includes(k)).sort();
}

export function getLastActivity(id: string): number | null {
  const raw = safeGet(lastActivityKey(id));
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function markActivity(id: string, now = Date.now()): void {
  safeSet(lastActivityKey(id), String(now));
}

/** After a reset the page is at its defaults: nothing to reset until it is used again. */
export function clearActivity(id: string): void {
  try {
    localStorage.removeItem(lastActivityKey(id));
  } catch {
    // Storage blocked.
  }
}

/** True when the page has had no input for IDLE_RESET_MS. A page never used has nothing to reset. */
export function isIdleExpired(id: string, now = Date.now()): boolean {
  const last = getLastActivity(id);
  return last !== null && now - last >= IDLE_RESET_MS;
}

/** The page's current inputs as stored. */
export function readValues(cfg: SessionCalculator): Record<string, string> {
  const values: Record<string, string> = {};
  for (const k of inputKeys(cfg)) {
    const v = safeGet(k);
    if (v !== null) values[k] = v;
  }
  return values;
}

export function readSharedSettings(cfg: SessionCalculator, settings: GlobalSettings): RecentCalculation['settings'] {
  if (!cfg.settings?.length) return undefined;
  const out: Partial<Pick<GlobalSettings, SharedSetting>> = {};
  for (const s of cfg.settings) (out as Record<string, unknown>)[s] = settings[s];
  return out;
}

/** Clears the page's inputs so it mounts with its defaults. */
export function clearInputs(cfg: SessionCalculator): void {
  for (const k of inputKeys(cfg)) {
    try {
      localStorage.removeItem(k);
    } catch {
      // Storage blocked.
    }
  }
}

/** Replaces the page's inputs with a recent's (the page must remount to read them). */
export function writeValues(cfg: SessionCalculator, values: Record<string, string>): void {
  clearInputs(cfg);
  for (const [k, v] of Object.entries(values)) {
    if (k.startsWith(cfg.prefix) && !cfg.keep?.includes(k)) safeSet(k, v);
  }
}

export function loadRecents(id: string): RecentCalculation[] {
  try {
    const parsed: unknown = JSON.parse(safeGet(recentsKey(id)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is RecentCalculation =>
        !!r && typeof r === 'object' && typeof r.id === 'string' && typeof r.at === 'number' &&
        !!r.values && typeof r.values === 'object',
    );
  } catch {
    return [];
  }
}

export function saveRecents(id: string, recents: RecentCalculation[]): void {
  safeSet(recentsKey(id), JSON.stringify(recents.slice(0, RECENTS_KEPT)));
}

type Calculation = Pick<RecentCalculation, 'values' | 'settings'>;

/** The page's inputs as it mounts with nothing stored; never listed as a recent. */
export function loadDefaults(id: string): Calculation | null {
  try {
    const parsed = JSON.parse(safeGet(defaultsKey(id)) ?? 'null');
    return parsed && typeof parsed === 'object' && parsed.values && typeof parsed.values === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function saveDefaults(id: string, defaults: Calculation): void {
  safeSet(defaultsKey(id), JSON.stringify(defaults));
}

/** Same inputs and same shared settings. */
export function sameCalculation(a: Calculation, b: Calculation): boolean {
  const ak = Object.keys(a.values);
  const bk = Object.keys(b.values);
  if (ak.length !== bk.length || ak.some(k => a.values[k] !== b.values[k])) return false;
  const as = a.settings ?? {};
  const bs = b.settings ?? {};
  const sk = new Set([...Object.keys(as), ...Object.keys(bs)]);
  for (const k of sk) {
    if ((as as Record<string, unknown>)[k] !== (bs as Record<string, unknown>)[k]) return false;
  }
  return true;
}

/**
 * Puts a calculation at the top of the list. One already listed with the same
 * inputs moves up (taking the newer label and time) instead of repeating.
 */
export function pushRecent(recents: RecentCalculation[], entry: RecentCalculation): RecentCalculation[] {
  return [entry, ...recents.filter(r => !sameCalculation(r, entry))].slice(0, RECENTS_KEPT);
}

/** Recents to list: newest first, without the one on screen. */
export function visibleRecents(
  recents: RecentCalculation[],
  current: Calculation | null,
): RecentCalculation[] {
  return recents.filter(r => !current || !sameCalculation(r, current)).slice(0, RECENTS_SHOWN);
}

export function newRecentId(now = Date.now()): string {
  return `${now}-${Math.random().toString(36).slice(2, 8)}`;
}

/** "Just now", "4 min ago", "2 h ago", "3 d ago". */
export function formatAgo(at: number, now = Date.now()): string {
  const mins = Math.floor((now - at) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** "Primary FLA 90.2 A · Breaker 125 A" from the banner's first tiles. */
export function summarizeTiles(tiles: AnswerTileData[], count = 2): string {
  return tiles
    .filter(t => !t.muted && t.variant !== 'spec')
    .slice(0, count)
    .map(t => `${t.label} ${t.value}${t.unit ? ` ${t.unit}` : ''}`)
    .join(' · ');
}
